import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, XCircle } from "lucide-react";

const POLL_MS = 4000;

/**
 * Ação sensível esperando a autorização do responsável legal (backend/legal_approval.py).
 * O interceptor de lib/api.js dispara "legal-approval-required" com `resume`;
 * aqui o pedido é acompanhado e, aprovado, a ação continua sozinha.
 */
export default function LegalApprovalPrompt() {
  const [req, setReq] = useState(null);      // detail do 428 + resume
  const [status, setStatus] = useState("pending");
  const timer = useRef(null);
  const resumed = useRef(false);

  useEffect(() => {
    const handler = (ev) => {
      // Um pedido por vez: se já há um aberto, o novo desiste (a tela avisa).
      if (req) { ev.detail.resume(false, "Já há uma ação esperando autorização. Conclua ou feche antes."); return; }
      resumed.current = false;
      setStatus("pending");
      setReq(ev.detail);
    };
    window.addEventListener("legal-approval-required", handler);
    return () => window.removeEventListener("legal-approval-required", handler);
  }, [req]);

  useEffect(() => {
    if (!req) return undefined;
    const poll = async () => {
      try {
        const { data } = await api.get(`/legal/approvals/${req.approval_id}`);
        setStatus(data.status);
        if (data.status === "approved" && !resumed.current) {
          resumed.current = true;
          req.resume(true);
          setReq(null);
          return;
        }
        if (data.status === "denied" || data.status === "expired") return;
      } catch (_) { /* tenta de novo no próximo ciclo */ }
      timer.current = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => clearTimeout(timer.current);
  }, [req]);

  const close = () => {
    if (req && !resumed.current) {
      resumed.current = true;
      req.resume(false, status === "denied"
        ? "O responsável legal não autorizou. A ação não foi feita."
        : "A ação não foi feita ainda. Quando o responsável autorizar, repita a ação em até 24 horas.");
    }
    setReq(null);
  };

  return (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md" data-testid="legal-approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" /> Autorização do responsável legal
          </DialogTitle>
        </DialogHeader>
        {req && (
          <div className="space-y-3 text-sm">
            <p>{req.message}</p>
            {status === "denied" ? (
              <p className="flex items-center gap-2 text-rose-600"><XCircle className="w-4 h-4" /> {req.rep_name || "O responsável"} não autorizou. Nada foi feito.</p>
            ) : status === "expired" ? (
              <p className="text-amber-700">O pedido expirou. Feche e repita a ação para pedir de novo.</p>
            ) : (
              <>
                <p className="text-muted-foreground">
                  Enviamos um pedido para {req.rep_name || "o responsável legal"} ({req.sent_to}). Assim que ele autorizar pelo
                  link do e-mail, a ação continua sozinha. Pode deixar esta janela aberta.
                </p>
                <p className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Aguardando autorização…</p>
              </>
            )}
            <Button variant="outline" className="w-full rounded-full" onClick={close} data-testid="legal-approval-close">
              Fechar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
